<template>
  <div class="d-flex flex-column ga-6 pa-6 home create-page h-100 flex-1-1" :class="{auth: userStore.isAuth}">
    <div class="d-flex flex-column ga-2 align-center">
      <template v-if="userStore.isAuth">
        <div class="title">{{ props.new ? $t('Создайте свою первую страницу') : $t('Создайте новую страницу') }}</div>
        <div class="sub-title">
          {{ $t('Введите название, которое будет в адресе вашей страницы. Его можно изменить в любое время') }}
        </div>
      </template>
      <template v-else>
        <div class="title">{{ $t('Создайте свою личную страницу') }}</div>
        <div class="sub-title">{{ $t('Соберите информацию о себе или компании в одном профиле и делитесь им в сети') }}
        </div>
      </template>
    </div>
    <div class="d-flex flex-column ga-2">
      <v-text-field
          ref="inputRef"
          variant="outlined"
          :placeholder="$t('название страницы')"
          :error-messages="$t(errorMessage)"
          hide-details="auto"
          autocapitalize="none"
          :prefix="`${host}/`"
          density="compact"
          rounded="lg"
          bg-color="#fff"
          @input="name = name.replace(/\s/g, '').toLowerCase()"
          v-model="name"
          class="name-field"
          @update:model-value="checkAlias"
          clearable
          maxlength="30"
      >
        <template v-slot:clear>
          <v-hover v-if="!nameLoading">
            <template v-slot:default="{ props }">
              <v-icon :icon="ClearIcon" v-bind="props" @click="name = '';clearError()" v-if="!!variants.length || error"/>
            </template>
          </v-hover>
        </template>
        <template v-slot:append-inner>
          <v-fade-transition leave-absolute>
            <v-icon :icon="SuccessIcon" size="20" v-if="name && !variants.length && !nameLoading && !error"/>
            <v-progress-circular v-else-if="nameLoading" size="24" :width="2" indeterminate color="primary" bg-color="transparent"/>
          </v-fade-transition>
        </template>
        <template #message>
          <div class="error-text">
            <template v-if="errorCode != 403">
              {{ $t(errorMessage) }}
            </template>
            <i18n-t keypath="В адресе есть недопустимые слова. Чтобы использовать этот адрес, свяжитесь со " v-else>
              <template #support>
                <span @click="openRocket" class="link">
                  {{ $t('службой поддержки') }}
                </span>
              </template>
            </i18n-t>
          </div>
        </template>
        <template #details>
          <v-menu v-model="showMenu" v-if="!!variants.length" :target="inputRef">
            <div class="variants">
              <div v-for="variant in variants" class="variant" @click="updateName(variant)">
                <div>{{variant}}</div>
                <v-icon :icon="SuccessIcon" size="20"/>
              </div>
            </div>
          </v-menu>
        </template>
      </v-text-field>

      <v-btn variant="flat" color="primary" rounded="lg" :disabled="progress" class="create-button"
             @click="checkAuthAndCreatePage()">
        <template v-if="progress">
          <v-progress-circular indeterminate size="18" :width="2" color="white" bg-color="transparent" class="mr-2"/>
          {{ $t('Создание страницы...') }}
        </template>
        <template v-else>
          {{ userStore.isAuth ? $t('Создать страницу') : $t('Создать бесплатно') }}
        </template>
      </v-btn>
    </div>
    <v-btn variant="plain" color="accent" @click="openDialog" v-if="!userStore.isAuth">
      {{ $t('Войти') }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref, watch} from "vue";
import ClearIcon from '~/assets/icons/clear.vue'
import SuccessIcon from '~/assets/icons/success.vue'
import {useUserStore} from '@/stores/userStore';
import {useTapStore} from '@/stores/tapStore';
import {useDialogStore} from "@/stores/dialogStore.js";
import Auth from "@/components/dialogs/Auth.vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
const router = useRouter();
const route = useRoute();
const props = defineProps({
  name: { name: String, default: '' },
  new: { name: Boolean, default: true },
  openInEditor: { type: Boolean, default: false }
})
const inputRef = ref(null)
const name = ref('')
const error = ref(false)
const errorMessage = ref('')
const errorCode = ref(0)

const progress = ref(false)
const showMenu = ref(false)
const config = useRuntimeConfig()
const host = config.public.tapLink;

const dialogStore = useDialogStore()
const userStore = useUserStore();
const tapStore = useTapStore();
const openDialog = () => dialogStore.open(Auth, null, `/profile`)
const openRocket = () => {
  //@ts-nocheck
  RocketChat(function() {
    this.showWidget();
    this.maximizeWidget();
  });
}


const checkAuthAndCreatePage = () => {
  if(error.value || !name.value) {
    if(!name.value) {
      clearError()
      variants.value = []
      error.value = true
      errorMessage.value = 'Ссылка может содержать от 3 до 30 символов и состоять из латинских букв, цифр и символов «.»и «_»'
      errorCode.value = 400
    }
    return
  }
  if(!userStore.isAuth) {
    dialogStore.open(Auth, null, `/profile/${name.value.replace(/\s/g, '').toLowerCase()}`)
  } else {
    createPage()
  }
}

const variants = ref<string[]>([])
const nameLoading = ref(false)
const checkAlias = () => {
  if(!name.value) {
    clearError()
    return
  }

  nameLoading.value = true
  tapStore.checkAlias(name.value.replace(/\s/g, '').toLowerCase()).then(result => {
    variants.value = result;
    error.value = result.length > 0
    showMenu.value = error.value
    errorMessage.value = error.value ? 'Такой адрес уже используется' : ''
    errorCode.value = 0
  }).catch(e => {
    variants.value = []
    error.value = true
    if(e.status == 400) {
      errorMessage.value = 'Ссылка может содержать от 3 до 30 символов и состоять из латинских букв, цифр и символов «.»и «_»'
      errorCode.value = e.status
    }
    if(e.status == 403) {
      errorMessage.value = 'error'
      errorCode.value = e.status
    }
  }).finally(() => {
    nameLoading.value = false
  })
}
const { locale } = useI18n()
const createPage = () => {
  if(!name.value) {
    clearError()
    variants.value = []
    error.value = true
    errorMessage.value = 'Ссылка может содержать от 3 до 30 символов и состоять из латинских букв, цифр и символов «.»и «_»'
    errorCode.value = 400
    return
  }

  progress.value = true
  tapStore.createTap(name.value.replace(/\s/g, '').toLowerCase(), locale.value == 'kk' ? 'kz' : locale.value).then(async (data) => {
    data['name'] = name.value.replace(/\s/g, '').toLowerCase()
    if (props.openInEditor) {
      await tapStore.getTapList({ preserveCurrentTap: true })
      await router.replace({ path: `/profile/${data.name}/edit`, query: route.query })
      dialogStore.close(true)
      return
    }
    tapStore.setTap(data)
    if(dialogStore.isOpen) {
      dialogStore.setRoute(`/profile/${data.name}/image`)
      dialogStore.close()
    } else {
      router.replace(`/profile/${data.name}/image`)
    }
  }).catch(e => {
    errorCode.value = 400
    if(e.status == 403) {
      if(e.data.error.messageEn === 'Alias already taken') {
        errorMessage.value = 'Такой адрес уже используется'
      } else {
        errorMessage.value = 'error'
        errorCode.value = 403
      }
    } else {
      errorMessage.value = 'Ссылка может содержать от 3 до 30 символов и состоять из латинских букв, цифр и символов «.»и «_»'
    }
    error.value = true
    progress.value = false
  })
}

const clearError = () => {
  error.value = false
  errorMessage.value = ''
  errorCode.value = 0
  variants.value = []
}

const updateName = (variant: any) => {
  name.value = variant.replace(/\s/g, '').toLowerCase()
  checkAlias()
}

onMounted(() => {
  if(props.name) {
    name.value = props.name.replace(/\s/g, '').toLowerCase()
    createPage()
  }
  if (window.matchMedia('(min-width: 600px) and (pointer: fine)').matches) inputRef.value?.focus()
})

watch(() => props.name, (n) => {
  name.value = n.replace(/\s/g, '').toLowerCase()
  createPage()
})

</script>

<style scoped lang="sass">
.home
  background: #fff

  &.auth
    margin: 16px 0
    border-radius: 16px

  .title
    color: #000
    text-align: center
    font-size: 20px
    font-weight: 600
    line-height: normal
    max-width: 225px

  .sub-title
    color: #363636
    text-align: center
    font-size: 14px
    font-weight: 400
    line-height: normal
    width: 100%

  .name-field
    ::v-deep
      .v-input__control
        .v-text-field__prefix
          color: #363636
          opacity: 1
          font-size: 14px
          font-weight: 400
          line-height: 20px

        .v-field__input
          padding-inline: 0 var(--v-field-padding-end)

  .create-button.v-btn--disabled.v-btn--variant-flat
    background-color: #0D87EF !important
    color: #fff !important

.link
  text-decoration: underline
  cursor: pointer
.variants
  border-radius: 8px
  border: 1px solid #CED4DA
  padding: 4px 0
  background: #FFF
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.12)
  .variant
    padding: 10px 16px
    gap: 8px
    display: flex
    justify-content: space-between
    color: #363636
    font-size: 14px
    line-height: 20px
    &:hover
      background: #F0F1F5
      cursor: pointer
</style>
